/**
 * Template Validation Engine — checks a composition result before it is
 * handed to the editor. Returns typed issues, never mutates anything.
 *
 * Checks: overflow, unsafe margins, missing required grammar nodes,
 * object overlap (text on text), alignment discipline, empty required text.
 */

import { rectInside, rectOverlapArea } from './layoutEngine';
import { GRAMMAR_NODES } from './designGrammarEngine';
import { alignmentConsistency } from './alignmentEngine';

export const SEVERITY = { ERROR: 'error', WARNING: 'warning' };

const issue = (severity, code, message, subject = null) => ({ severity, code, message, subject });

/**
 * Validate a compose() result.
 * Returns { valid, errors, warnings, issues[] } — valid means zero errors.
 */
export const validateComposition = ({ commands, layout, placed }) => {
  const issues = [];
  const canvasRect = { x: 0, y: 0, width: layout.canvas.width, height: layout.canvas.height };

  // Content placements only — structural markers and decorations play by
  // different rules (decorations may bleed off-canvas).
  const content = placed.filter((p) => !p.structural && !p.nodeType.startsWith('component:'));
  const allContent = placed.filter((p) => !p.structural);

  // 1. Overflow: nothing except decorations may leave the canvas.
  allContent.forEach((p) => {
    if (!rectInside(p.rect, canvasRect)) {
      issues.push(issue(SEVERITY.ERROR, 'overflow',
        `"${p.nodeType}" overflows the canvas`, p.commandId));
    }
  });

  // 2. Safe margins: grammar text content must respect the safe area.
  content.forEach((p) => {
    if (!rectInside(p.rect, layout.safeArea)) {
      issues.push(issue(SEVERITY.WARNING, 'unsafe-margin',
        `"${p.nodeType}" crosses the safe margin`, p.commandId));
    }
  });

  // 3. Missing required grammar nodes.
  const emitted = new Set(placed.map((p) => p.nodeType));
  GRAMMAR_NODES.filter((n) => n.required && n.zone).forEach((n) => {
    if (!emitted.has(n.type)) {
      issues.push(issue(SEVERITY.ERROR, 'missing-node',
        `required grammar node "${n.type}" was not emitted`));
    }
  });
  if (!commands.some((c) => c.command === 'createCanvas')) {
    issues.push(issue(SEVERITY.ERROR, 'missing-node', 'no createCanvas command'));
  }

  // 4. Empty text on required text nodes.
  content.forEach((p) => {
    if (p.text === false) {
      issues.push(issue(SEVERITY.WARNING, 'empty-text',
        `"${p.nodeType}" has no text content`, p.commandId));
    }
  });

  // 5. Overlap between content objects (>20% of the smaller rect).
  for (let i = 0; i < content.length; i++) {
    for (let j = i + 1; j < content.length; j++) {
      const a = content[i], b = content[j];
      const overlap = rectOverlapArea(a.rect, b.rect);
      const smaller = Math.min(a.rect.width * a.rect.height, b.rect.width * b.rect.height);
      if (smaller > 0 && overlap / smaller > 0.2) {
        issues.push(issue(SEVERITY.ERROR, 'overlap',
          `"${a.nodeType}" overlaps "${b.nodeType}"`, `${a.commandId},${b.commandId}`));
      }
    }
  }

  // 6. Alignment discipline across text content.
  const consistency = alignmentConsistency(content.map((p) => p.rect));
  if (consistency < 0.75) {
    issues.push(issue(SEVERITY.WARNING, 'alignment',
      `only ${Math.round(consistency * 100)}% of content shares an alignment axis`));
  }

  const errors = issues.filter((i) => i.severity === SEVERITY.ERROR);
  const warnings = issues.filter((i) => i.severity === SEVERITY.WARNING);
  return { valid: errors.length === 0, errors, warnings, issues };
};
